import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";

import styled from "styled-components/macro";

import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { useSignups } from "Signups/state";
import { motion } from "framer-motion";

import { colors } from "UI/colors";

import LoginModal from "./LoginModal";
import NewRespondentPopover from "./NewRespondentPopover";
import RelationshipModalOnly from "./RelationshipModalOnly";

export default function AddRespondentButton({
  disabled,
  addNewRespondent,
  addNewHouseholdRespondent,
  isAnonymousUser,
  userData,
  numberOfRespondents,
}) {
  const [showPopover, setShowPopover] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [showLoginPopover, setShowLoginPopover] = useState(false);
  const [state] = useSignups();
  const buttonRef = useRef();
  const hasNoRespondents = numberOfRespondents === 0;

  const handleClick = () => {
    if (window.data.loginLevel === "partial") {
      setShowLoginPopover(true);
    } else if (
      (signupHasExtendedFields() || hasNoRespondents) &&
      userData.networkHasFNC
    ) {
      setShowRelationshipModal(true);
    } else if (!isAnonymousUser && userData.networkHasFNC) {
      setShowPopover(true);
    } else if (hasNoRespondents) {
      // shouldn't happen, but show an error message if it does?
      // can't add additional users without at least one respondent selected
    } else {
      addNewRespondent();
    }
  };

  const handlePopoverClose = () => {
    setShowPopover(false);
  };

  const signupHasExtendedFields = () => {
    if (!state?.currentSignupList || !state?.currentSignupList?.signupItems)
      return false;
    for (const signupItem of state.currentSignupList.signupItems) {
      if (signupItem.extendedFields?.length) return true;
    }
    return false;
  };

  return (
    <>
      {!hasNoRespondents || userData.networkHasFNC ? (
        <Wrapper
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 1 }}
          transition={{ ease: "easeIn", duration: 0.2 }}
          ref={buttonRef}
        >
          <Inside>
            <Button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              data-cy="public-signup-add-respondent"
              show={!hasNoRespondents || userData.networkHasFNC}
            >
              <AddIconWrapper>
                <AddIcon icon={faPlusCircle} />
              </AddIconWrapper>
              Add someone else...
            </Button>
          </Inside>
        </Wrapper>
      ) : null}
      <NewRespondentPopover
        buttonRef={buttonRef}
        show={showPopover}
        onClose={handlePopoverClose}
        addNewRespondent={addNewRespondent}
        addNewHouseholdRespondent={addNewHouseholdRespondent}
        userID={userData?.ID}
      />
      <RelationshipModalOnly
        show={showRelationshipModal}
        setShow={setShowRelationshipModal}
        userID={userData?.ID}
        addNewHouseholdRespondent={addNewHouseholdRespondent}
      />
      <LoginModal
        show={showLoginPopover}
        setShow={setShowLoginPopover}
        userEmail={userData.email || ""}
      />
    </>
  );
}

const Wrapper = styled(motion.div)`
  position: relative;
  z-index: 1000;
`;
const Inside = styled.div`
  padding: 10px 10px 12px;
`;

const Button = styled.button`
  display: inline-flex;
  cursor: pointer;
  color: ${colors.fnDkBlue};
  font-weight: 400;
  opacity: 0.4;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  padding: 0;
  margin-left: 1px;
  font-size: 16px;
  transition: color 250ms, opacity 250ms;

  &:hover {
    opacity: 1;
    color: ${colors.fnBlue};
  }

  &:focus {
    outline: none;
    color: ${colors.fnBlue};
    opacity: 1;
    transform: scale(1.02);
  }
`;

const AddIconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const AddIcon = styled(FontAwesomeIcon)`
  font-size: 21px;
`;
